/**
 * The Google, GitHub and Microsoft sign-in row used by Login and Signup.
 * Shared so the buttons live in one place. It shows a button only for
 * each provider in `providers`, which the screens take from the public
 * settings: the ones the server has keys for. A provider without keys
 * would fail when clicked, so it is never shown. With none, the row
 * renders nothing, and the screens drop their "or" line too.
 *
 * Each button shows the logo and the name; its accessible name keeps
 * the full "Continue with ...". Side by side only when the row is at
 * least 330px wide, because "Microsoft" and its logo take about 85px
 * inside a button, sharing the row equally. Narrower than that (most
 * phones), one per line. The breakpoint is a container query on the
 * row's own width, not the viewport's, so it holds wherever the row is
 * placed. Each logo is `shrink-0`: without it the flex layout squeezed a
 * logo to 1px whenever its name ran wide, and the button read as text
 * only. OttoCloud's sign-in screens use the same layout and sizes.
 *
 * The `disabled` prop disables ALL buttons together — calling code
 * sets it during in-flight redirects so a fast double-click can't fire
 * two `window.location` assignments.
 */
import type { ComponentType } from 'react'
import { Button } from '@/components/ui'
import { GitHubIcon } from '@/auth/GitHubIcon'
import { GoogleIcon } from '@/auth/GoogleIcon'
import { MicrosoftIcon } from '@/auth/MicrosoftIcon'
import type { SignInProvider } from '@/lib/api'

export type OAuthProvider = SignInProvider

export interface OAuthRowProps {
  /** The providers to show, in any order; the row keeps its own order. */
  providers: OAuthProvider[]
  onSelect: (provider: OAuthProvider) => void
  disabled?: boolean
}

const PROVIDERS: { id: OAuthProvider; label: string; Icon: ComponentType<{ className?: string }> }[] = [
  { id: 'google', label: 'Google', Icon: GoogleIcon },
  { id: 'github', label: 'GitHub', Icon: GitHubIcon },
  { id: 'microsoft', label: 'Microsoft', Icon: MicrosoftIcon },
]

/** Spelled out so Tailwind finds each class: one column per button shown. */
const COLUMNS = ['', '@min-[330px]:grid-cols-1', '@min-[330px]:grid-cols-2', '@min-[330px]:grid-cols-3']

export function OAuthRow({ providers, onSelect, disabled }: OAuthRowProps) {
  const shown = PROVIDERS.filter(({ id }) => providers.includes(id))
  if (shown.length === 0) return null
  return (
    <div className="@container">
      <div className={`grid grid-cols-1 gap-2 ${COLUMNS[shown.length]}`}>
        {shown.map(({ id, label, Icon }) => (
          <Button
            key={id}
            variant="outline"
            type="button"
            onClick={() => onSelect(id)}
            disabled={disabled}
            aria-label={`Continue with ${label}`}
            className="w-full gap-1.5 px-2"
          >
            <Icon className="h-[18px] w-[18px] shrink-0" />
            {label}
          </Button>
        ))}
      </div>
    </div>
  )
}
